import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  alpha,
  useTheme,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Storefront as StoreIcon,
  ListAlt as OrderIcon,
  AddCircle as AddIcon,
  Loyalty as CollectionIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  LocalShipping as PrepIcon,
  AccountCircle as UserIcon,
  People as PartnersIcon,
  AccountBalanceWallet as PayoutIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  TrendingUp as TrendIcon,
  BarChart as ChartIcon,
  ShowChart as LineIcon,
  QueryStats as StatsIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  getSellerInventory,
  createListing,
  updateListing,
  deleteListing,
  getSellerOrders,
  updateOrderStatus,
  StoreListing,
  StoreOrder,
  getPartnerLedger,
  PartnerLedgerRow,
  getSalesAnalytics,
  SalesAnalyticsResponse,
  StoreUser,
  getStoreUsers,
  updateStoreUserRole,
} from '../../services/storeService';
import { useEnrichedCollection, EnrichedOwnership } from '../../hooks/useEnrichedCollection';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { cardSearchService } from '../../services/cardSearchService';
import { opCardSearchService } from '../../services/opCardSearchService';
import { useMarketPricing } from '../../hooks/useMarketPricing';
import EventManagementTab from './components/EventManagementTab';

interface SearchResultCardProps {
  card: any;
  searchGame: 'pokemon' | 'onepiece';
  onList: (card: any, gameType: 'pokemon' | 'onepiece', livePrice: number | null) => void;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({ card, searchGame, onList }) => {
  const theme = useTheme();
  
  // Extract number, name, setId, rarity, justtcgId
  const cardNumber = card.number || '';
  const cardName = card.name || '';
  const setId = card.setId || '';
  const rarity = card.rarity || '';
  const justtcgId = card.justtcgId || '';

  // Get live price
  const { data: pricingData, isLoading: loadingPrice } = useMarketPricing(
    cardNumber,
    cardName,
    setId,
    rarity,
    true,
    undefined,
    justtcgId
  );

  // Fallback static price determination
  let staticPrice: number | null = null;
  if (searchGame === 'pokemon') {
    const p = card.tcgplayer?.prices || card.prices;
    if (p) {
      const prices = Object.values(p);
      if (prices.length > 0) {
        staticPrice = (prices[0] as any)?.market || (prices[0] as any)?.mid || null;
      }
    }
  } else {
    staticPrice = card.prices?.market || card.prices?.mid || null;
  }

  const livePrice = pricingData?.marketPrice ?? staticPrice;
  const cardImageUrl = card.images?.large || card.images?.small || card.imageUrl || '';

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        sx={{
          background: 'rgba(255,255,255,0.02)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
          <Box sx={{ flexShrink: 0, width: 60, height: 82, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img
              src={cardImageUrl}
              alt={card.name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://images.pokemontcg.io/xy12/logo.png';
              }}
            />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight="bold" noWrap title={card.name}>
              {card.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap title={card.setName}>
              {card.setName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                label={searchGame === 'pokemon' ? 'Pokémon' : 'One Piece'}
                size="small"
                color={searchGame === 'pokemon' ? 'primary' : 'secondary'}
                sx={{ height: 18, fontSize: 9, fontWeight: 'bold' }}
              />
              {card.number && (
                <Chip
                  label={`#${card.number}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 9, borderColor: 'rgba(255,255,255,0.1)' }}
                />
              )}
              {card.rarity && (
                <Chip
                  label={card.rarity}
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={{ height: 18, fontSize: 9 }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Est. Market Value</Typography>
            {loadingPrice ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={12} color="primary" />
              </Box>
            ) : (
              <Typography variant="body2" fontWeight="bold" color="primary">
                {livePrice !== null ? `$${livePrice.toFixed(2)}` : 'N/A'}
              </Typography>
            )}
          </Box>

          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onList(card, searchGame, livePrice)}
            sx={{
              borderRadius: 2,
              borderColor: alpha(theme.palette.primary.main, 0.4),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                background: alpha(theme.palette.primary.main, 0.05),
              }
            }}
          >
            List for Retail Sale
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};

const StoreAdmin: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const { isAuthenticated, setAuthModalOpen, user } = useAuth();

  const userGroups = user?.groups || [];
  const isSuperAdminOrOwner = userGroups.includes('SuperAdmins') || userGroups.includes('Owners');

  // Tab State
  const [activeTab, setActiveTab] = useState<number>(0);

  // Data States
  const [inventory, setInventory] = useState<StoreListing[]>([]);
  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [ledger, setLedger] = useState<PartnerLedgerRow[]>([]);
  
  const [loadingInventory, setLoadingInventory] = useState<boolean>(false);
  const [loadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [loadingLedger, setLoadingLedger] = useState<boolean>(false);
  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);

  // Collection Data State (for listing easily)
  const { data: ownedCards = [], loading: loadingCollection } = useEnrichedCollection();

  // Dialog / Modal States
  const [isListingModalOpen, setIsListingModalOpen] = useState<boolean>(false);
  const [editingListing, setEditingListing] = useState<StoreListing | null>(null);

  // Listing Form State
  const [cardName, setCardName] = useState<string>('');
  const [cardNumber, setCardNumber] = useState<string>('');
  const [setName, setSetName] = useState<string>('');
  const [cardId, setCardId] = useState<string>('');
  const [setId, setSetId] = useState<string>('');
  const [game, setGame] = useState<'pokemon' | 'onepiece'>('pokemon');
  const [price, setPrice] = useState<string>('');
  const [stockQuantity, setStockQuantity] = useState<string>('1');
  const [condition, setCondition] = useState<string>('NM');
  const [language, setLanguage] = useState<string>('EN');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [rarity, setRarity] = useState<string>('');
  const [status, setStatus] = useState<'active' | 'draft'>('active');
  const [cardOwner, setCardOwner] = useState<string>('Store');
  const [customPartner, setCustomPartner] = useState<string>('');
  const [customPartnersList, setCustomPartnersList] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('chasingtcg_custom_partners');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [];
  });
  const [priceType, setPriceType] = useState<'fixed' | 'market'>('fixed');

  const [savingListing, setSavingListing] = useState<boolean>(false);

  // Global search states for Admin listing from search catalog
  const [globalSearchQuery, setGlobalSearchQuery] = useState<string>('');
  const [searchGame, setSearchGame] = useState<'pokemon' | 'onepiece'>('pokemon');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchingCards, setSearchingCards] = useState<boolean>(false);

  // Consignment Rate split and Analytics states
  const [consignmentRate, setConsignmentRate] = useState<string>('10');
  const [salesData, setSalesData] = useState<SalesAnalyticsResponse | null>(null);
  const [loadingSales, setLoadingSales] = useState<boolean>(false);

  // Load Inventory & Orders
  const loadInventory = async () => {
    if (!isAuthenticated) return;
    setLoadingInventory(true);
    try {
      const data = await getSellerInventory();
      setInventory(data);
      
      // Auto-extract and learn custom partners from active inventory listings
      const inventoryPartners = data
        .map(listing => listing.cardOwner)
        .filter((name): name is string => !!name && name !== 'Store');
      
      if (inventoryPartners.length > 0) {
        setCustomPartnersList(prev => {
          const combined = Array.from(new Set([...prev, ...inventoryPartners]));
          localStorage.setItem('chasingtcg_custom_partners', JSON.stringify(combined));
          return combined;
        });
      }
    } catch (error) {
      showNotification('Failed to fetch inventory listings', 'error');
    } finally {
      setLoadingInventory(false);
    }
  };

  const loadOrders = async () => {
    if (!isAuthenticated) return;
    setLoadingOrders(true);
    try {
      const data = await getSellerOrders();
      setOrders(data);
    } catch (error) {
      showNotification('Failed to fetch customer orders', 'error');
    } finally {
      setLoadingOrders(false);
    }
  };

  const loadLedger = async () => {
    if (!isAuthenticated || !isSuperAdminOrOwner) return;
    setLoadingLedger(true);
    try {
      const data = await getPartnerLedger();
      setLedger(data);
      
      // Auto-extract and learn custom partners from ledger rows
      const ledgerPartners = data
        .map(row => row.partnerName)
        .filter((name): name is string => !!name && name !== 'Store');
      
      if (ledgerPartners.length > 0) {
        setCustomPartnersList(prev => {
          const combined = Array.from(new Set([...prev, ...ledgerPartners]));
          localStorage.setItem('chasingtcg_custom_partners', JSON.stringify(combined));
          return combined;
        });
      }
    } catch (error) {
      showNotification('Failed to fetch consignment partner ledger', 'error');
    } finally {
      setLoadingLedger(false);
    }
  };

  const loadAnalytics = async () => {
    if (!isAuthenticated) return;
    setLoadingSales(true);
    try {
      const data = await getSalesAnalytics();
      setSalesData(data);
    } catch (error) {
      showNotification('Failed to fetch sales analytics data', 'error');
    } finally {
      setLoadingSales(false);
    }
  };

  const loadUsers = async () => {
    if (!isAuthenticated || !isSuperAdminOrOwner) return;
    setLoadingUsers(true);
    try {
      const data = await getStoreUsers();
      setStoreUsers(data);
    } catch (error) {
      showNotification('Failed to fetch store users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 0) {
        loadAnalytics();
      } else if (activeTab === 1) {
        loadInventory();
      } else if (activeTab === 2) {
        loadOrders();
      } else if (activeTab === 5 && isSuperAdminOrOwner) {
        loadLedger();
      } else if (activeTab === 6 && isSuperAdminOrOwner) {
        loadUsers();
      }
    }
  }, [activeTab, isAuthenticated, isSuperAdminOrOwner]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      loadAnalytics();
      loadInventory();
      loadOrders();
      if (isSuperAdminOrOwner) {
        loadLedger();
        if (activeTab === 6) loadUsers();
      }
    }
  }, [isAuthenticated, isSuperAdminOrOwner]);

  // Open modal for new manual listing
  const handleOpenNewListing = () => {
    setEditingListing(null);
    setCardName('');
    setCardNumber('');
    setSetName('');
    setCardId('');
    setSetId('');
    setGame('pokemon');
    setPrice('');
    setStockQuantity('1');
    setCondition('NM');
    setLanguage('EN');
    setImageUrl('');
    setRarity('');
    setStatus('active');
    setCardOwner('Store');
    setCustomPartner('');
    setPriceType('fixed');
    setConsignmentRate('10');
    setIsListingModalOpen(true);
  };

  // Open modal to quick-list directly from physical collection
  const handleListFromCollection = (card: EnrichedOwnership) => {
    setEditingListing(null);
    setCardName(card.cardName);
    setCardNumber(card.number);
    setSetName(card.setName);
    setCardId(card.cardId);
    setSetId(card.setId || '');
    setGame(card.game);
    setPrice(card.currentValue ? card.currentValue.toString() : '');
    setStockQuantity(card.quantity.toString());
    setCondition(card.condition || 'NM');
    setLanguage(card.language || 'EN');
    setImageUrl(card.imageUrl || '');
    setRarity(card.rarity || '');
    setStatus('active');
    setCardOwner('Store');
    setCustomPartner('');
    setPriceType('market');
    setConsignmentRate('10');
    setIsListingModalOpen(true);
    showNotification(`Pre-filled listing with card details from your collection`, 'info');
  };

  // Search Global Catalog Handlers
  const handleSearchCards = async () => {
    if (!globalSearchQuery || globalSearchQuery.trim().length < 2) {
      showNotification('Please enter at least 2 characters to search', 'warning');
      return;
    }
    setSearchingCards(true);
    try {
      if (searchGame === 'pokemon') {
        const res = await cardSearchService.searchCards(globalSearchQuery);
        setSearchResults(res.cards || []);
      } else {
        const res = await opCardSearchService.searchCards(globalSearchQuery);
        setSearchResults(res.cards || []);
      }
    } catch (error) {
      showNotification('Error searching cards from global database', 'error');
      setSearchResults([]);
    } finally {
      setSearchingCards(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchCards();
    }
  };

  const handleListFromSearch = (card: any, gameType: 'pokemon' | 'onepiece', livePrice: number | null = null) => {
    setEditingListing(null);
    setCardName(card.name);
    setCardNumber(card.number || '');
    setSetName(card.setName || '');
    setCardId(card.id || card.cardId || 'manual-' + Date.now());
    setSetId(card.setId || '');
    setGame(gameType);
    
    // Extract price
    let extractedPrice = '';
    if (livePrice !== null) {
      extractedPrice = livePrice.toString();
    } else {
      if (gameType === 'pokemon') {
        const p = card.tcgplayer?.prices || card.prices;
        if (p) {
          const prices = Object.values(p);
          if (prices.length > 0) {
            const val = (prices[0] as any)?.market || (prices[0] as any)?.mid || null;
            if (val !== null) {
              extractedPrice = val.toString();
            }
          }
        }
      } else {
        // onepiece
        const val = card.prices?.market || card.prices?.mid || null;
        if (val !== null) {
          extractedPrice = val.toString();
        }
      }
    }
    
    setPrice(extractedPrice || '');
    setStockQuantity('1');
    setCondition('NM');
    setLanguage('EN');
    setImageUrl(card.images?.large || card.images?.small || card.imageUrl || '');
    setRarity(card.rarity || '');
    setStatus('active');
    setCardOwner('Store');
    setCustomPartner('');
    setPriceType('market');
    setConsignmentRate('10');
    setIsListingModalOpen(true);
    showNotification(`Pre-filled listing with card details from global search`, 'info');
  };

  // Open modal to edit listing
  const handleOpenEditListing = (listing: StoreListing) => {
    setEditingListing(listing);
    setCardName(listing.cardName);
    setCardNumber(listing.number);
    setSetName(listing.setName);
    setCardId(listing.cardId);
    setSetId(listing.setId);
    setGame(listing.game);
    setPrice(listing.price.toString());
    setStockQuantity(listing.stockQuantity.toString());
    setCondition(listing.condition);
    setLanguage(listing.language);
    setImageUrl(listing.imageUrl);
    setRarity(listing.rarity || '');
    setStatus(listing.status);
    setPriceType(listing.priceType || 'fixed');
    setConsignmentRate(listing.consignmentRate ? listing.consignmentRate.toString() : '10');
    
    const ownerVal = listing.cardOwner || 'Store';
    if (ownerVal === 'Store' || customPartnersList.includes(ownerVal)) {
      setCardOwner(ownerVal);
      setCustomPartner('');
    } else {
      // If it's a new custom partner from another device, learn it
      if (ownerVal && ownerVal !== 'Store') {
        const newList = Array.from(new Set([...customPartnersList, ownerVal]));
        setCustomPartnersList(newList);
        localStorage.setItem('chasingtcg_custom_partners', JSON.stringify(newList));
      }
      setCardOwner(ownerVal);
      setCustomPartner('');
    }
    setIsListingModalOpen(true);
  };

  // Handle Save Listing (Create or Update)
  const handleSaveListing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardName || !setName || !price || !stockQuantity || !imageUrl) {
      showNotification('Please fill in all required fields', 'warning');
      return;
    }

    setSavingListing(true);
    try {
      const finalCardOwner = cardOwner === 'custom' ? (customPartner.trim() || 'Store') : cardOwner;
      
      // Persist and remember the custom partner if newly typed
      if (cardOwner === 'custom' && customPartner.trim()) {
        const trimmed = customPartner.trim();
        if (trimmed !== 'Store' && !customPartnersList.includes(trimmed)) {
          const newList = [...customPartnersList, trimmed];
          setCustomPartnersList(newList);
          localStorage.setItem('chasingtcg_custom_partners', JSON.stringify(newList));
        }
      }

      const listingData = {
        cardName,
        number: cardNumber,
        setName,
        cardId: cardId || 'manual-' + Date.now(),
        setId: setId || 'manual-set',
        game,
        price: parseFloat(price),
        stockQuantity: parseInt(stockQuantity),
        condition,
        language,
        imageUrl,
        rarity,
        status,
        cardOwner: finalCardOwner,
        priceType,
        consignmentRate: finalCardOwner === 'Store' ? undefined : parseFloat(consignmentRate || '10'),
      };

      if (editingListing) {
        await updateListing(editingListing.listingId, listingData);
        showNotification('Listing updated successfully', 'success');
      } else {
        await createListing(listingData);
        showNotification('Listing created successfully!', 'success');
      }

      setIsListingModalOpen(false);
      loadInventory();
      loadAnalytics();
      if (isSuperAdminOrOwner) {
        loadLedger();
      }
    } catch (error: any) {
      showNotification(error.message || 'Failed to save listing', 'error');
    } finally {
      setSavingListing(false);
    }
  };

  // CSV Export for tax purposes
  const handleExportCSV = () => {
    if (!salesData || !salesData.orders || salesData.orders.length === 0) {
      showNotification('No sales records available to export', 'warning');
      return;
    }

    const headers = [
      'Order ID',
      'Purchase Date',
      'Customer Name',
      'Customer Email',
      'Card Name',
      'Game',
      'Condition',
      'Quantity',
      'Sale Price ($)',
      'Owner / Consigner',
      'Total Item Sales ($)',
      'Consignment Split Rate (%)',
      'Store Commission Split ($)',
      'Partner Payout Owed ($)'
    ];

    const csvRows = [];
    csvRows.push(headers.join(','));

    for (const order of salesData.orders) {
      for (const item of order.items) {
        const quantity = item.quantity;
        const price = item.price;
        const totalItemSale = price * quantity;
        const isStoreOwned = item.cardOwner === 'Store';
        const rate = isStoreOwned ? 0 : (item.consignmentRate !== undefined ? item.consignmentRate : 10);
        const storeFee = isStoreOwned ? totalItemSale : totalItemSale * (rate / 100);
        const partnerPayout = isStoreOwned ? 0 : totalItemSale - storeFee;

        const row = [
          `"${order.orderId}"`,
          `"${new Date(order.createdAt).toLocaleDateString()}"`,
          `"${order.customerName.replace(/"/g, '""')}"`,
          `"${order.customerEmail.replace(/"/g, '""')}"`,
          `"${item.cardName.replace(/"/g, '""')}"`,
          `"${item.game === 'pokemon' ? 'Pokémon' : 'One Piece'}"`,
          `"${item.condition}"`,
          quantity,
          price.toFixed(2),
          `"${item.cardOwner.replace(/"/g, '""')}"`,
          totalItemSale.toFixed(2),
          rate,
          storeFee.toFixed(2),
          partnerPayout.toFixed(2)
        ];

        csvRows.push(row.join(','));
      }
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `store_tax_report_${new Date().getFullYear()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification('Excel CSV Tax Report downloaded successfully', 'success');
  };

  // Delete Listing
  const handleDeleteListing = async (listingId: string) => {
    if (!window.confirm('Are you sure you want to delete this listing? This will remove it from the public storefront.')) return;
    try {
      await deleteListing(listingId);
      showNotification('Listing deleted', 'info');
      loadInventory();
      if (isSuperAdminOrOwner) {
        loadLedger();
      }
    } catch (error) {
      showNotification('Failed to delete listing', 'error');
    }
  };

  // Update Order Status
  const handleUpdateOrderStatus = async (orderId: string, status: 'ready' | 'completed' | 'cancelled') => {
    try {
      await updateOrderStatus(orderId, status);
      showNotification(`Order marked as ${status}`, 'success');
      loadOrders();
      loadInventory(); // Restored cancellations affect stock quantity!
      if (isSuperAdminOrOwner) {
        loadLedger();
      }
    } catch (error: any) {
      showNotification(error.message || 'Failed to update order status', 'error');
    }
  };

  // Update User Role
  const handleUpdateUserRole = async (username: string, newRole: string) => {
    try {
      await updateStoreUserRole(username, newRole);
      showNotification(`User role updated to ${newRole}`, 'success');
      loadUsers();
    } catch (error: any) {
      showNotification(error.message || 'Failed to update user role', 'error');
    }
  };

  // Status Chip Color Helper
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending Pickup" color="warning" variant="outlined" size="small" />;
      case 'ready':
        return <Chip label="Ready for Pickup" color="secondary" size="small" />;
      case 'completed':
        return <Chip label="Completed" color="success" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" variant="outlined" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // Render Login Prompt if Unauthenticated
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '65vh',
          textAlign: 'center',
          p: 4,
        }}
      >
        <Paper
          sx={{
            p: 5,
            maxWidth: 450,
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: 5,
          }}
        >
          <StoreIcon color="primary" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Store Inventory Manager
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Please sign in to list cards for sale, adjust prices, edit retail stocks, and manage buyer pickup orders.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setAuthModalOpen(true)}
            sx={{ py: 1.2, px: 5, borderRadius: 3, fontWeight: 'bold' }}
          >
            Sign In Now
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '85vh', pb: 8 }}>
      {/* Header bar */}
      <Box
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StoreIcon color="primary" /> Store Admin Panel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your card listings, view live collection cards to sell, and fulfill customer pickup reservations.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenNewListing}
            sx={{ py: 1.2, px: 3, borderRadius: 3 }}
          >
            Add Manual Listing
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{
          mb: 4,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          '& .MuiTab-root': { fontWeight: 'bold', fontSize: 15 },
        }}
      >
        <Tab icon={<StatsIcon />} iconPosition="start" label="Sales Dashboard" />
        <Tab icon={<StoreIcon />} iconPosition="start" label="Store Inventory" />
        <Tab
          icon={<OrderIcon />}
          iconPosition="start"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Fulfillment Orders
              {orders.filter((o) => o.status === 'pending').length > 0 && (
                <Chip
                  label={orders.filter((o) => o.status === 'pending').length}
                  size="small"
                  color="error"
                  sx={{ height: 18, fontSize: 10, fontWeight: 'bold' }}
                />
              )}
            </Box>
          }
        />
        <Tab icon={<CollectionIcon />} iconPosition="start" label="List from My Collection" />
        <Tab icon={<SearchIcon />} iconPosition="start" label="Search & List" />
        {isSuperAdminOrOwner && (
          <Tab icon={<EventIcon />} iconPosition="start" label="Events & Tournaments" />
        )}
        {isSuperAdminOrOwner && (
          <Tab icon={<PartnersIcon />} iconPosition="start" label="Consignment Partners" />
        )}
        {isSuperAdminOrOwner && (
          <Tab icon={<UserIcon />} iconPosition="start" label="Staff & Users" />
        )}
      </Tabs>

      {/* TAB 0: SALES DASHBOARD */}
      {activeTab === 0 && (
        <Box>
          {loadingSales ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : !salesData ? (
            <Box
              sx={{
                py: 10,
                textAlign: 'center',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.01)',
                border: '1px dashed rgba(255,255,255,0.05)',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No sales data available.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Year End CSV Export Header Card */}
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(156, 39, 176, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PayoutIcon color="primary" /> Year-End Tax Reporting
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Export a fully itemized ledger of all completed pickup sales, partner payouts, and store commissions directly into Microsoft Excel.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCSV}
                  sx={{ py: 1, px: 3, borderRadius: 2, fontWeight: 'bold' }}
                >
                  Download Tax Report (CSV)
                </Button>
              </Box>

              {/* KPI Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Monthly Summary */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">This Month</Typography>
                        <TrendIcon color="primary" />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Gross Revenue</Typography>
                        <Typography variant="h5" fontWeight="bold">${salesData.summary.month.grossSales.toFixed(2)}</Typography>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Consignment Cost</Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main">-${salesData.summary.month.consignmentCost.toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Net Store Revenue</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">${salesData.summary.month.netRevenue.toFixed(2)}</Typography>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                      <Typography variant="caption" color="text.secondary">
                        Completed orders: <strong>{salesData.summary.month.orderCount}</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Yearly Summary */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">This Year ({new Date().getFullYear()})</Typography>
                        <ChartIcon color="secondary" />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Gross Revenue</Typography>
                        <Typography variant="h5" fontWeight="bold">${salesData.summary.year.grossSales.toFixed(2)}</Typography>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Consignment Cost</Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main">-${salesData.summary.year.consignmentCost.toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Net Store Revenue</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">${salesData.summary.year.netRevenue.toFixed(2)}</Typography>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                      <Typography variant="caption" color="text.secondary">
                        Completed orders: <strong>{salesData.summary.year.orderCount}</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Lifetime Summary */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">Lifetime Sales</Typography>
                        <PayoutIcon color="success" />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Gross Revenue</Typography>
                        <Typography variant="h5" fontWeight="bold">${salesData.summary.lifetime.grossSales.toFixed(2)}</Typography>
                      </Box>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Consignment Cost</Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main">-${salesData.summary.lifetime.consignmentCost.toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">Net Store Revenue</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">${salesData.summary.lifetime.netRevenue.toFixed(2)}</Typography>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
                      <Typography variant="caption" color="text.secondary">
                        Completed orders: <strong>{salesData.summary.lifetime.orderCount}</strong>
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Graphical Visualization using Recharts */}
              <Card sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 3, mb: 4, p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Monthly Financial Trends
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  Real-time visualization of monthly gross sales, consignment splits, and net store commission revenue.
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                  <ResponsiveContainer>
                    <AreaChart
                      data={salesData.monthlyTrend}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorGross" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.4}/>
                          <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4caf50" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#4caf50" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f44336" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#f44336" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" style={{ fontSize: 11 }} />
                      <YAxis stroke="rgba(255,255,255,0.5)" style={{ fontSize: 11 }} tickFormatter={(val) => `$${val}`} />
                      <Tooltip
                        contentStyle={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Legend style={{ fontSize: 12 }} />
                      <Area name="Gross Revenue" type="monotone" dataKey="grossSales" stroke={theme.palette.primary.main} fillOpacity={1} fill="url(#colorGross)" />
                      <Area name="Consignment Cost" type="monotone" dataKey="consignmentCost" stroke="#f44336" fillOpacity={1} fill="url(#colorCost)" />
                      <Area name="Net Store Profit" type="monotone" dataKey="netRevenue" stroke="#4caf50" fillOpacity={1} fill="url(#colorNet)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Card>

              {/* Monthly Breakdown Table */}
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Monthly Financial Ledger
              </Typography>
              <TableContainer
                component={Paper}
                sx={{
                  background: 'rgba(255,255,255,0.02)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 3,
                }}
              >
                <Table>
                  <TableHead sx={{ background: 'rgba(255,255,255,0.02)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Calendar Month</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Pickups Fulfilled</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Gross Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Consignment Splits Owed</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Net Store Profit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.monthlyTrend.slice().reverse().map((row) => (
                      <TableRow key={row.month} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.month}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.orderCount} orders</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            ${row.grossSales.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            -${row.consignmentCost.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            ${row.netRevenue.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* TAB 1: STORE INVENTORY */}
      {activeTab === 1 && (
        <Box>
          {loadingInventory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : inventory.length === 0 ? (
            <Box
              sx={{
                py: 10,
                textAlign: 'center',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.01)',
                border: '1px dashed rgba(255,255,255,0.05)',
              }}
            >
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No cards listed for sale yet.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                List cards manually or quick-sell them straight from your collection!
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setActiveTab(3)}
                startIcon={<CollectionIcon />}
              >
                Browse My Collection
              </Button>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                background: 'rgba(255,255,255,0.02)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: 3,
              }}
            >
              <Table>
                <TableHead sx={{ background: 'rgba(255,255,255,0.02)' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Card Info</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Game</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Condition</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Retail Stock</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Owner</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory.map((listing) => (
                    <TableRow key={listing.listingId} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            variant="square"
                            src={listing.imageUrl}
                            sx={{ width: 40, height: 55, borderRadius: 1, objectFit: 'contain' }}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {listing.cardName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {listing.setName} • #{listing.number}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={listing.game === 'pokemon' ? 'Pokémon' : 'One Piece'}
                          size="small"
                          color={listing.game === 'pokemon' ? 'primary' : 'secondary'}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {listing.condition} ({listing.language})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          ${listing.price.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color={listing.stockQuantity > 0 ? 'text.primary' : 'error'}
                        >
                          {listing.stockQuantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={listing.cardOwner || 'Store'}
                          size="small"
                          color={(!listing.cardOwner || listing.cardOwner === 'Store') ? 'default' : 'primary'}
                          variant="outlined"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={listing.status === 'active' ? 'Active' : 'Draft'}
                          size="small"
                          color={listing.status === 'active' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" size="small" onClick={() => handleOpenEditListing(listing)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="error" size="small" onClick={() => handleDeleteListing(listing.listingId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TAB 2: CUSTOMER ORDERS */}
      {activeTab === 2 && (
        <Box>
          {loadingOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : orders.length === 0 ? (
            <Box
              sx={{
                py: 10,
                textAlign: 'center',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.01)',
                border: '1px dashed rgba(255,255,255,0.05)',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No customer pickup orders placed yet.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {orders.map((order) => (
                <Grid item xs={12} key={order.orderId}>
                  <Card
                    sx={{
                      background: 'rgba(255, 255, 255, 0.02)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderRadius: 3,
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Order info header */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            {order.orderId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Date: {new Date(order.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getStatusChip(order.status)}
                          <Typography variant="h6" fontWeight="bold">
                            Total: ${order.totalAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />

                      {/* Customer info & items list */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <UserIcon fontSize="small" /> Customer Contact Info
                          </Typography>
                          <Typography variant="body2">Name: {order.customerName}</Typography>
                          <Typography variant="body2">Email: {order.customerEmail}</Typography>
                          <Typography variant="body2">Phone: {order.customerPhone}</Typography>
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                            Reserved Cards:
                          </Typography>
                          {order.items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                              <img
                                src={item.imageUrl}
                                alt={item.cardName}
                                style={{ width: 24, height: 33, objectFit: 'contain', borderRadius: 2 }}
                              />
                              <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
                                <strong>{item.quantity}x</strong> {item.cardName} ({item.condition}) • <span style={{ opacity: 0.7, fontSize: '0.85em' }}>Owner: {item.cardOwner || 'Store'}</span>
                              </Typography>
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                ${(item.price * item.quantity).toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                        </Grid>

                        {/* Actions controls */}
                        <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                          {order.status === 'pending' && (
                            <>
                              <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<PrepIcon />}
                                size="small"
                                onClick={() => handleUpdateOrderStatus(order.orderId, 'ready')}
                              >
                                Mark Ready
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={() => handleUpdateOrderStatus(order.orderId, 'cancelled')}
                              >
                                Cancel Order
                              </Button>
                            </>
                          )}

                          {order.status === 'ready' && (
                            <>
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={<CompleteIcon />}
                                size="small"
                                onClick={() => handleUpdateOrderStatus(order.orderId, 'completed')}
                              >
                                Complete Pickup
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={() => handleUpdateOrderStatus(order.orderId, 'cancelled')}
                              >
                                Cancel Order
                              </Button>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 3: LIST FROM COLLECTION */}
      {activeTab === 3 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Select a card from your digital collection to sell
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Directly lists cards to the retail storefront. Pre-fills conditions, quantity, and live pricing.
          </Typography>

          {loadingCollection ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : ownedCards.length === 0 ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.01)',
                border: '1px dashed rgba(255,255,255,0.05)',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Your collection has no cards registered.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {ownedCards.map((card) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={card.ownershipId}>
                  <Card
                    sx={{
                      background: 'rgba(255,255,255,0.02)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-4px)' },
                    }}
                  >
                    <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                      <img
                        src={card.imageUrl}
                        alt={card.cardName}
                        style={{ width: 60, height: 82, objectFit: 'contain', borderRadius: 4 }}
                      />
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {card.cardName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {card.setName}
                        </Typography>
                        <Chip
                          label={card.game === 'pokemon' ? 'Pokémon' : 'One Piece'}
                          size="small"
                          color={card.game === 'pokemon' ? 'primary' : 'secondary'}
                          sx={{ height: 18, fontSize: 10, mt: 1, fontWeight: 'bold' }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ px: 2, pb: 2 }}>
                      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.06)' }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Owned</Typography>
                          <Typography variant="body2" fontWeight="bold">{card.quantity} copies</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" display="block">Value</Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary">${card.currentValue?.toFixed(2) || '0.00'}</Typography>
                        </Box>
                      </Box>

                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<StoreIcon />}
                        onClick={() => handleListFromCollection(card)}
                        sx={{ borderRadius: 2 }}
                      >
                        List for Retail Sale
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 4: SEARCH & LIST INVENTORY */}
      {activeTab === 4 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Search Global TCG Catalog to Add Store Inventory
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Instantly list any card for sale by searching the official Pokémon or One Piece database. Pre-fills card details and live market values.
          </Typography>

          {/* Search Bar Controls */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
              p: 2,
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search card by name or number (e.g. Charizard, OP01-001)..."
              value={globalSearchQuery}
              onChange={(e) => setGlobalSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
            />
            
            <TextField
              select
              value={searchGame}
              onChange={(e) => {
                setSearchGame(e.target.value as 'pokemon' | 'onepiece');
                setSearchResults([]);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="pokemon">Pokémon TCG</MenuItem>
              <MenuItem value="onepiece">One Piece TCG</MenuItem>
            </TextField>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchCards}
              disabled={searchingCards}
              sx={{ px: 4, minWidth: 120, height: 56, borderRadius: 2 }}
            >
              {searchingCards ? <CircularProgress size={24} color="inherit" /> : 'Search'}
            </Button>
          </Box>

          {searchingCards ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : searchResults.length === 0 ? (
            <Box
              sx={{
                py: 10,
                textAlign: 'center',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.01)',
                border: '1px dashed rgba(255,255,255,0.05)',
              }}
            >
              <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="body1" color="text.secondary">
                {globalSearchQuery ? 'No cards found matching your search.' : 'Type a card name to search the global catalog.'}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {searchResults.map((card) => (
                <SearchResultCard
                  key={card.id || card.cardId}
                  card={card}
                  searchGame={searchGame}
                  onList={handleListFromSearch}
                />
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* TAB 6: CONSIGNMENT PARTNERS */}
      {activeTab === 6 && isSuperAdminOrOwner && (
        <Box>
          {loadingLedger ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : ledger.length === 0 ? (
            <Box
              sx={{
                py: 10,
                textAlign: 'center',
                borderRadius: 4,
                background: 'rgba(255,255,255,0.01)',
                border: '1px dashed rgba(255,255,255,0.05)',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No consignment partner ledger entries found.
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">Consigned Active Value</Typography>
                        <StoreIcon color="primary" sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        ${ledger.reduce((acc, row) => acc + (row.partnerName !== 'Store' ? row.activeValue : 0), 0).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        In active partner listings
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">Total Completed Payouts</Typography>
                        <PayoutIcon color="success" sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        ${ledger.reduce((acc, row) => acc + (row.partnerName !== 'Store' ? row.owedPayout : 0), 0).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Completed sales payouts owed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">Consigned Stock Count</Typography>
                        <PartnersIcon color="secondary" sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {ledger.reduce((acc, row) => acc + (row.partnerName !== 'Store' ? row.activeCount : 0), 0)} pcs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Partner items currently listed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">Active Consigners</Typography>
                        <PartnersIcon color="primary" sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {ledger.filter(row => row.partnerName !== 'Store').length} Partners
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excluding store inventory
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Ledger Table */}
              <TableContainer
                component={Paper}
                sx={{
                  background: 'rgba(255,255,255,0.02)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.05)',
                  borderRadius: 3,
                }}
              >
                <Table>
                  <TableHead sx={{ background: 'rgba(255,255,255,0.02)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Partner / Owner Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Active Listing Stock</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Active Valuation</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Completed Sales Payout</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Items Sold</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.map((row) => (
                      <TableRow key={row.partnerName} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {row.partnerName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.activeCount} items</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            ${row.activeValue.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            ${row.owedPayout.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.completedCount} items</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.partnerName === 'Store' ? 'Retail / Internal' : 'Consignment Partner'}
                            size="small"
                            color={row.partnerName === 'Store' ? 'default' : 'secondary'}
                            variant="outlined"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}

      {/* TAB 7: STAFF & USERS */}
      {activeTab === 7 && isSuperAdminOrOwner && (
        <Box>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Staff & Users Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View all registered users and assign roles such as Employee, Admin, or Owner.
              </Typography>
            </Box>
          </Box>

          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : storeUsers.length === 0 ? (
            <Box sx={{ py: 10, textAlign: 'center' }}>
              <Typography color="text.secondary">No users found.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ background: 'rgba(255, 255, 255, 0.02)', borderRadius: 3 }}>
              <Table>
                <TableHead sx={{ background: 'rgba(0, 0, 0, 0.2)' }}>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Current Roles</TableCell>
                    <TableCell align="right">Manage Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeUsers.map((u) => {
                    // Determine the primary role for the dropdown
                    const managedRoles = ['Owners', 'SuperAdmins', 'Admins', 'Employees'];
                    let primaryRole = 'User';
                    for (const role of managedRoles) {
                      if (u.groups.includes(role)) {
                        primaryRole = role;
                        break;
                      }
                    }

                    return (
                      <TableRow key={u.username} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Typography fontWeight="bold">{u.username}</Typography>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          {u.groups.length > 0 ? (
                            u.groups.map(g => (
                              <Chip key={g} label={g} size="small" color={g === 'Owners' ? 'error' : g === 'Admins' || g === 'SuperAdmins' ? 'warning' : 'primary'} sx={{ mr: 1 }} />
                            ))
                          ) : (
                            <Chip label="User" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            select
                            size="small"
                            value={primaryRole}
                            onChange={(e) => handleUpdateUserRole(u.username, e.target.value)}
                            sx={{ minWidth: 140 }}
                            disabled={u.username === user?.username} // prevent self-demotion
                          >
                            <MenuItem value="User">User</MenuItem>
                            <MenuItem value="Employees">Employee</MenuItem>
                            <MenuItem value="Admins">Admin</MenuItem>
                            <MenuItem value="SuperAdmins">SuperAdmin</MenuItem>
                            <MenuItem value="Owners">Owner</MenuItem>
                          </TextField>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* TAB 5: EVENTS & TOURNAMENTS */}
      {activeTab === 5 && isSuperAdminOrOwner && (
        <Box sx={{ p: 2 }}>
          <EventManagementTab />
        </Box>
      )}

      {/* Listing Form Modal */}
      <Dialog
        open={isListingModalOpen}
        onClose={() => setIsListingModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(20, 20, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {editingListing ? 'Edit Listing Details' : 'Create New Store Listing'}
        </DialogTitle>
        <form onSubmit={handleSaveListing}>
          <DialogContent sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: 3 }}>
            <Grid container spacing={3}>
              {/* Product Info Section */}
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                  1. Card Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Card Name"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Card Number"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      required
                      label="Set Name"
                      value={setName}
                      onChange={(e) => setSetName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="TCG Game"
                      value={game}
                      onChange={(e) => setGame(e.target.value as 'pokemon' | 'onepiece')}
                    >
                      <MenuItem value="pokemon">Pokémon TCG</MenuItem>
                      <MenuItem value="onepiece">One Piece Card Game</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Rarity"
                      value={rarity}
                      onChange={(e) => setRarity(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Image URL"
                      placeholder="https://images.pokemontcg.io/..."
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Pricing & Stock Section */}
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                  2. Pricing & Availability
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Pricing Model"
                      value={priceType}
                      onChange={(e) => setPriceType(e.target.value as 'fixed' | 'market')}
                    >
                      <MenuItem value="fixed">Fixed Price (Set manually)</MenuItem>
                      <MenuItem value="market">Live Market Price (Auto-tracks live value)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Retail Price ($)"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      disabled={priceType === 'market'}
                      helperText={priceType === 'market' ? 'Price automatically tracks live TCG market fluctuations.' : ''}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Retail Stock Quantity"
                      inputProps={{ min: '1' }}
                      value={stockQuantity}
                      onChange={(e) => setStockQuantity(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Condition"
                      value={condition}
                      onChange={(e) => setCondition(e.target.value)}
                    >
                      <MenuItem value="NM">Near Mint (NM)</MenuItem>
                      <MenuItem value="LP">Lightly Played (LP)</MenuItem>
                      <MenuItem value="MP">Moderately Played (MP)</MenuItem>
                      <MenuItem value="HP">Heavily Played (HP)</MenuItem>
                      <MenuItem value="G">Graded</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      fullWidth
                      label="Language"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <MenuItem value="EN">English</MenuItem>
                      <MenuItem value="JP">Japanese</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Listing Status"
                      value={status}
                      onChange={(e) => setStatus(e.target.value as 'active' | 'draft')}
                    >
                      <MenuItem value="active">Active (Listed in public store)</MenuItem>
                      <MenuItem value="draft">Draft (Hidden)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      label="Card Owner / Consigner"
                      value={cardOwner}
                      onChange={(e) => {
                        setCardOwner(e.target.value);
                        if (e.target.value !== 'custom') {
                          setCustomPartner('');
                        }
                      }}
                    >
                      <MenuItem value="Store">Store Owned</MenuItem>
                      {customPartnersList.map((partner) => (
                        <MenuItem key={partner} value={partner}>
                          {partner}
                        </MenuItem>
                      ))}
                      <MenuItem value="custom">Custom Partner...</MenuItem>
                    </TextField>
                  </Grid>
                  {cardOwner === 'custom' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Custom Partner Name"
                        placeholder="Enter partner name"
                        value={customPartner}
                        onChange={(e) => setCustomPartner(e.target.value)}
                      />
                    </Grid>
                  )}
                  {cardOwner !== 'Store' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        label="Store Commission Split (%)"
                        placeholder="Enter split percentage (e.g. 10)"
                        inputProps={{ min: '0', max: '100', step: '0.1' }}
                        value={consignmentRate}
                        onChange={(e) => setConsignmentRate(e.target.value)}
                        helperText="The percentage fee retained by the store upon checkout. Partners receive the remainder."
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setIsListingModalOpen(false)} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={savingListing}
              sx={{ px: 4, borderRadius: 2 }}
            >
              {savingListing ? <CircularProgress size={20} /> : 'Save Listing'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default StoreAdmin;
